/**
 * IconService - Centralizes iconography for Athena.
 * Manages Lucide icons (static import) and official brand SVGs.
 */
import { createIcons, icons } from 'lucide';

// SVG Oficial Azure DevOps (Infinity Blue)
const AZURE_SVG = `
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 19.33L4.67 22L12 18.67L19.33 22L22 19.33L12 2Z" fill="#0078D4"/>
    <path opacity="0.8" d="M12 2L6.67 14.67L12 18.67L17.33 14.67L12 2Z" fill="#50A2E2"/>
</svg>`;

// SVG Oficial GitLab (Tanuki Orange)
const GITLAB_SVG = `
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.65 14.39L20.5 7.76C20.39 7.42 19.92 7.42 19.81 7.76L17.65 14.39H6.35L4.19 7.76C4.08 7.42 3.61 7.42 3.5 7.76L1.35 14.39C1.24 14.73 1.36 15.1 1.66 15.32L11.66 22.58C11.86 22.72 12.13 22.72 12.33 22.58L22.33 15.32C22.63 15.1 22.75 14.73 22.65 14.39Z" fill="#E24329"/>
    <path d="M12 22.58L22.33 15.32C22.63 15.1 22.75 14.73 22.65 14.39L12 12V22.58Z" fill="#FC6D26"/>
    <path d="M12 22.58L1.66 15.32C1.36 15.1 1.25 14.73 1.35 14.39L12 12V22.58Z" fill="#FC6D26"/>
    <path d="M12 12L6.35 14.39H17.65L12 12Z" fill="#FCA326"/>
</svg>`;

// Mapeamento: slug -> nome do ícone Lucide ou SVG de marca
const ICON_MAP: Record<string, string> = {
    // Brand Slugs (SVG inline)
    'brand-azure': AZURE_SVG,
    'brand-gitlab': GITLAB_SVG,

    // Action Slugs -> Lucide icon names
    'athena-brain': 'brain',
    'athena-think': 'brain',
    'athena-search': 'search',
    'athena-read': 'book-open',
    'athena-write': 'edit-3',
    'athena-file': 'file-text',
    'athena-folder': 'folder',
    'athena-git': 'git-branch',
    'athena-code': 'code-2',
    'athena-check': 'check-circle-2',
    'athena-alert': 'alert-triangle',
    'athena-error': 'x-circle',
    'athena-zap': 'zap',
    'athena-sparkle': 'sparkles',
    'athena-message': 'message-square',
    'athena-question': 'help-circle',
    'athena-eye': 'eye',
    'athena-settings': 'settings',

    // Legacy Emoji Fallbacks (Compatibility Layer)
    '🧠': 'brain',
    '💭': 'message-square',
    '🔍': 'search',
    '🔎': 'search',
    '📄': 'file-text',
    '📖': 'book-open',
    '📁': 'folder',
    '📂': 'folder',
    '🔀': 'git-branch',
    '📝': 'edit-3',
    '✏️': 'edit-3',
    '🔃': 'rotate-cw',
    '✅': 'check-circle-2',
    '⚠️': 'alert-triangle',
    '❌': 'x-circle',
    '⚡': 'zap',
    '⚙️': 'settings',
    '🦊': GITLAB_SVG,
    '☁️': AZURE_SVG,
};

export class IconService {
    /**
     * Renders an icon from a slug or emoji.
     * For brand SVGs, returns the raw SVG string with adjusted size.
     * For Lucide icons, returns a <i data-lucide="..."> tag — call refresh() after DOM insertion.
     *
     * @param identifier Slug (e.g., 'athena-search') or Emoji (e.g., '🔍')
     * @param size Size in pixels
     * @param color CSS color (optional, only applies to Lucide icons)
     */
    public static render(identifier: string, size: number = 14, color: string = 'currentColor'): string {
        const target = ICON_MAP[identifier] ?? identifier;

        // Brand SVG — resize and return inline
        if (target.startsWith('<svg')) {
            return target
                .replace(/width="?24"?/, `width="${size}"`)
                .replace(/height="?24"?/, `height="${size}"`);
        }

        // Lucide icon — return placeholder tag for createIcons()
        return `<i data-lucide="${target}" style="width:${size}px;height:${size}px;display:block;" stroke="${color}"></i>`;
    }

    /**
     * Converts all <i data-lucide="..."> tags inside the container into SVGs.
     * Must be called AFTER the container is inserted into the DOM.
     */
    public static refresh(container: HTMLElement): void {
        createIcons({
            icons,
            attrs: {
                'stroke-width': '2.5',
                'stroke-linecap': 'round',
                'stroke-linejoin': 'round',
            },
            nameAttr: 'data-lucide',
            root: container,
        });
    }
}
